import React, { useRef, useCallback } from 'react';
import { ATTRIBUTE_COLORS, TYPE_ICONS, CARD_FRAMES } from '../utils/constants';
import styles from '../styles/gameStyles';
import mobileStyles from '../styles/mobileStyles';
import { CARD_SIZE } from '../styles/breakpoints';
import { getStatusIcon, getStatusDisplayName } from '../engine/statusEffects';
import { hasHikou, hasTaiku } from '../engine/keywordAbilities';
import AttributeIcon from './AttributeIcon';
import { useCardIllustration } from '../hooks/useCardIllustration';
import type { CardAttribute, StatusEffect } from '../types';

// ========================================
// フィールドモンスターコンポーネント
// ========================================

/** スキル情報 */
interface SkillInfo {
  text: string;
  cost?: number;
}

/** チャージ情報 */
interface ChargeInfo {
  isSPCharge?: boolean;
  attribute?: CardAttribute;
}

/** リンクダメージ情報 */
interface LinkedEndPhaseDamage {
  damage: number;
}

/** フィールドモンスターデータ */
interface FieldMonsterData {
  id: string;
  name: string;
  attribute: CardAttribute;
  hp: number;
  currentHp: number;
  currentAttack: number;
  basicSkill?: SkillInfo;
  advancedSkill?: SkillInfo;
  charges?: ChargeInfo[];
  linkedTo?: string;
  linkedEndPhaseDamage?: LinkedEndPhaseDamage[];
  statusEffects?: StatusEffect[];
  keyword?: string;
}

interface FieldMonsterProps {
  monster: FieldMonsterData | null;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  selected?: boolean;
  canAttack?: boolean;
  isTarget?: boolean;
  isValidTarget?: boolean;
  isDirectAttackTarget?: boolean;
  isTargetSelectable?: boolean;
  isTargetSelected?: boolean;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  atkModifier?: number;
  hpModifier?: number;
  isMobile?: boolean;
  onLongPress?: (monster: FieldMonsterData) => void;
}

const FieldMonster: React.FC<FieldMonsterProps> = ({
  monster,
  onClick,
  selected,
  canAttack,
  isTarget,
  isValidTarget,
  isDirectAttackTarget,
  isTargetSelectable,
  isTargetSelected,
  onMouseEnter,
  onMouseLeave,
  atkModifier = 0,
  hpModifier = 0,
  isMobile,
  onLongPress,
}) => {
  // 長押し検出用
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredAt = useRef<number>(0); // タイムスタンプで管理

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !onLongPress || !monster) return;
    longPressTriggeredAt.current = 0;
    longPressTimer.current = setTimeout(() => {
      longPressTriggeredAt.current = Date.now();
      onLongPress(monster);
    }, 500);
  }, [isMobile, onLongPress, monster]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    // 長押しだった場合はイベント伝播を止める
    if (longPressTriggeredAt.current > 0) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // 長押し後300ms以内のクリックは無視（タッチイベント→クリックイベントのタイミング差対策）
    if (longPressTriggeredAt.current > 0 && Date.now() - longPressTriggeredAt.current < 300) {
      longPressTriggeredAt.current = 0;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    longPressTriggeredAt.current = 0;
    if (onClick) {
      onClick(e);
    }
  }, [onClick]);

  // モバイル対応スロットスタイル
  const getSlotStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.cardSlot,
        ...mobileStyles.cardSlot,
      };
    }
    return styles.cardSlot;
  };

  const slotStyle = getSlotStyle();

  // イラストの有無をチェック（共通フックを使用）
  const { hasIllustration, illustrationPath, frameImage } = useCardIllustration(
    monster?.id,
    monster?.attribute || 'なし'
  );

  if (!monster) {
    const isHighlighted = isValidTarget || isDirectAttackTarget;
    return (
      <div
        style={{
          ...slotStyle,
          cursor: isHighlighted ? 'pointer' : 'default',
          borderWidth: isHighlighted ? '2px' : (isMobile ? '1px' : '2px'),
          borderStyle: 'dashed',
          borderColor: isDirectAttackTarget ? '#ff4444' : isValidTarget ? '#6b4ce6' : 'rgba(107,76,230,0.4)',
          background: isDirectAttackTarget ? 'rgba(255,68,68,0.2)' : isValidTarget ? 'rgba(107,76,230,0.2)' : 'rgba(20,20,40,0.5)',
          transition: 'all 0.3s ease',
          fontSize: isMobile ? '9px' : '12px',
          color: isDirectAttackTarget ? '#ff4444' : '#6b4ce6',
          fontWeight: isHighlighted ? 'bold' : 'normal',
          boxShadow: isDirectAttackTarget ? '0 0 10px rgba(255,68,68,0.5)' : 'none',
        }}
        onClick={onClick}
      >
        {isDirectAttackTarget ? (isMobile ? '直接攻撃' : '直接攻撃可能') : isValidTarget ? (isMobile ? '召喚' : '召喚可能') : ''}
      </div>
    );
  }

  const colors = ATTRIBUTE_COLORS[monster.attribute] || ATTRIBUTE_COLORS['なし'];
  const hpPercent = (monster.currentHp / monster.hp) * 100;

  return (
    <div
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
      onTouchMove={isMobile ? handleTouchEnd : undefined}
      style={{
        ...slotStyle,
        borderWidth: isTargetSelected || isTargetSelectable || selected || isTarget ? '3px' : (isMobile ? '1px' : '2px'),
        borderStyle: 'solid',
        borderColor: isTargetSelected ? '#4caf50' : isTargetSelectable ? '#e91e63' : selected ? '#ffd700' : isTarget ? '#ff4444' : 'rgba(107,76,230,0.6)',
        // イラストがある場合はイラストを背景に、ない場合はsolid枠を背景に
        // 注意: background ショートハンドを使用して個別プロパティとの競合を防ぐ
        background: `url(${hasIllustration ? illustrationPath : frameImage}) center/cover no-repeat`,
        cursor: isTargetSelectable || isTargetSelected ? 'pointer' : 'pointer',
        flexDirection: 'column',
        padding: isMobile ? '2px' : '4px',
        position: 'relative',
        boxShadow: isTargetSelected
          ? '0 0 20px #4caf50, 0 0 30px rgba(76,175,80,0.5)'
          : isTargetSelectable
            ? '0 0 15px #e91e63, 0 0 25px rgba(233,30,99,0.4)'
            : selected
              ? `0 0 20px ${colors.glow}, 0 0 30px rgba(255,215,0,0.5)`
              : canAttack
                ? `0 0 15px ${colors.glow}`
                : 'none',
        animation: isTargetSelectable ? 'pulse 1.5s infinite' : canAttack ? 'pulse 2s infinite' : 'none',
        // モバイル長押し時のOSコンテキストメニュー抑制
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {/* イラストがある場合は透過フレームを前面に重ねる */}
      {hasIllustration && (
        <img
          src={frameImage}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
            zIndex: 1,
          }}
          draggable={false}
        />
      )}

      {/* 攻撃可能インジケーター */}
      {canAttack && (
        <div style={{
          position: 'absolute',
          top: '-8px',
          right: '-8px',
          background: '#ffd700',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          boxShadow: '0 0 10px #ffd700',
          zIndex: 10,
        }}>
          ⚔️
        </div>
      )}

      {/* チャージ表示 */}
      {monster.charges && monster.charges.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '-8px',
          left: '-8px',
          background: 'rgba(0,0,0,0.8)',
          borderRadius: '10px',
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          fontWeight: 'bold',
          color: '#fff',
          gap: '2px',
          zIndex: 10,
        }}>
          {monster.charges.map((charge, idx) => (
            <span key={idx} title={charge.isSPCharge ? 'SPチャージ' : `属性チャージ (${charge.attribute})`}>
              {charge.isSPCharge ? '💠' : '🃏'}
            </span>
          ))}
        </div>
      )}

      {/* 【魂結】リンク状態表示 */}
      {monster.linkedTo && (
        <div style={{
          position: 'absolute',
          top: '-8px',
          left: monster.charges && monster.charges.length > 0 ? '30px' : '-8px',
          background: 'linear-gradient(135deg, #9d4ce6 0%, #6b4ce6 100%)',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          boxShadow: '0 0 10px #9d4ce6',
          border: '1px solid rgba(255,255,255,0.3)',
          zIndex: 10,
        }}
        title={`【魂結】リンク中${monster.linkedEndPhaseDamage && monster.linkedEndPhaseDamage.length > 0 ? ` (ターン終了時${monster.linkedEndPhaseDamage.reduce((sum, d) => sum + d.damage, 0)}ダメージ)` : ''}`}
        >
          🔗
        </div>
      )}

      {/* 【飛行】【対空】キーワードアイコン */}
      {(hasHikou(monster) || hasTaiku(monster)) && (
        <div style={{
          position: 'absolute',
          top: '-6px',
          right: canAttack ? '16px' : '-6px',
          display: 'flex',
          gap: '2px',
          zIndex: 10,
        }}>
          {hasHikou(monster) && (
            <div style={{
              background: 'linear-gradient(135deg, #87CEEB 0%, #4682B4 100%)',
              borderRadius: '50%',
              width: isMobile ? '16px' : '18px',
              height: isMobile ? '16px' : '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '10px' : '11px',
              boxShadow: '0 0 8px rgba(135,206,235,0.8)',
              border: '1px solid rgba(255,255,255,0.4)',
            }}
            title="【飛行】地上モンスターから攻撃されない"
            >
              🕊️
            </div>
          )}
          {hasTaiku(monster) && (
            <div style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
              borderRadius: '50%',
              width: isMobile ? '16px' : '18px',
              height: isMobile ? '16px' : '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '10px' : '11px',
              boxShadow: '0 0 8px rgba(255,215,0,0.8)',
              border: '1px solid rgba(255,255,255,0.4)',
            }}
            title="【対空】飛行モンスターを攻撃可能"
            >
              🎯
            </div>
          )}
        </div>
      )}

      {/* 状態異常アイコン */}
      {monster.statusEffects && monster.statusEffects.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '2px',
          background: 'rgba(0,0,0,0.7)',
          borderRadius: '8px',
          padding: '2px 4px',
          zIndex: 10,
        }}>
          {monster.statusEffects.map((status, idx) => (
            <div
              key={status.id || `${status.type}-${idx}`}
              title={getStatusDisplayName(status.type)}
              style={{
                fontSize: '12px',
                lineHeight: 1,
                cursor: 'help',
              }}
            >
              {getStatusIcon(status.type)}
            </div>
          ))}
        </div>
      )}

      {/* カード名（枠に被らない位置に配置、属性色+縁取り） */}
      <div style={{
        position: 'absolute',
        top: isMobile ? '4px' : '6px',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: isMobile ? '8px' : '11px',
        fontWeight: 'bold',
        color: colors.text,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        maxWidth: '110%',
        overflow: 'hidden',
        zIndex: 10,
        // 光属性（暗い文字）は白縁、他（明るい文字）は黒縁
        textShadow: monster.attribute === '光'
          ? [
              '-1px -1px 0 #fff',
              '1px -1px 0 #fff',
              '-1px 1px 0 #fff',
              '1px 1px 0 #fff',
              '0 0 4px rgba(255,255,255,0.8)',
            ].join(', ')
          : [
              '-1px -1px 0 #000',
              '1px -1px 0 #000',
              '-1px 1px 0 #000',
              '1px 1px 0 #000',
              '0 0 4px rgba(0,0,0,0.8)',
            ].join(', '),
      }}>
        {monster.name}
      </div>

      <div style={{ marginBottom: isMobile ? '2px' : '4px', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px', zIndex: 10 }}>
        <AttributeIcon attribute={monster.attribute} size={isMobile ? 22 : 28} />
        {/* 技アイコン */}
        {(monster.basicSkill || monster.advancedSkill) && (
          <div style={{
            position: 'absolute',
            bottom: '-2px',
            right: '18px',
            display: 'flex',
            gap: '2px',
          }}>
            {monster.basicSkill && (
              <div style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                fontWeight: 'bold',
                color: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.3)',
              }} title={`基本技: ${monster.basicSkill.text}`}>
                1
              </div>
            )}
            {monster.advancedSkill && (
              <div style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                fontWeight: 'bold',
                color: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.3)',
              }} title={`上級技: ${monster.advancedSkill.text}`}>
                2
              </div>
            )}
          </div>
        )}
      </div>

      {/* HPバー */}
      <div style={{
        width: '90%',
        height: isMobile ? '4px' : '8px',
        background: 'rgba(0,0,0,0.5)',
        borderRadius: '4px',
        overflow: 'hidden',
        marginBottom: isMobile ? '2px' : '4px',
        zIndex: 10,
      }}>
        <div style={{
          width: `${hpPercent}%`,
          height: '100%',
          background: hpPercent > 50 ? '#4caf50' : hpPercent > 25 ? '#ff9800' : '#f44336',
          borderRadius: '4px',
          transition: 'width 0.3s ease',
        }} />
      </div>

      <div style={{
        display: 'flex',
        gap: isMobile ? '2px' : '8px',
        fontSize: isMobile ? '8px' : '10px',
        fontWeight: 'bold',
        background: 'rgba(0,0,0,0.5)',
        padding: isMobile ? '1px 2px' : '0',
        zIndex: 10,
      }}>
        <span style={{
          color: atkModifier > 0 ? '#4cff4c' : atkModifier < 0 ? '#ff4c4c' : '#fff',
          textShadow: atkModifier !== 0 ? `0 0 4px ${atkModifier > 0 ? '#4cff4c' : '#ff4c4c'}` : 'none',
        }}>
          {isMobile ? '' : '⚔️'}{monster.currentAttack + atkModifier}
          {!isMobile && atkModifier !== 0 && (
            <span style={{ fontSize: '8px', marginLeft: '1px' }}>
              ({atkModifier > 0 ? '+' : ''}{atkModifier})
            </span>
          )}
        </span>
        <span style={{
          color: hpModifier > 0 ? '#4cff4c' : hpModifier < 0 ? '#ff4c4c' : '#6bff6b',
          textShadow: hpModifier !== 0 ? `0 0 4px ${hpModifier > 0 ? '#4cff4c' : '#ff4c4c'}` : 'none',
        }}>
          {isMobile ? '' : '❤️'}{monster.currentHp}
          {!isMobile && hpModifier !== 0 && (
            <span style={{ fontSize: '8px', marginLeft: '1px' }}>
              ({hpModifier > 0 ? '+' : ''}{hpModifier})
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default FieldMonster;
